/**
 * WebSocket Event Converter Utility
 *
 * Converts WebSocket event details (EventCreatedEvent/EventUpdatedEvent) to
 * Event objects compatible with the EventItem component for display.
 *
 * Fields that the WebSocket payload does not carry (mediaKeys, thumbnailKeys,
 * createdAt, updatedAt) are filled with safe defaults here. When these objects
 * are used to update an existing cache entry the merge helpers in
 * event-cache-merge.ts always re-apply the real values from the cached entry, so the defaults are
 * only visible for brand-new events before the first full fetch completes.
 */

import type {
	EventSensor,
	EventStatus,
	EventWithSensors,
	InstallationDto,
	Measurement,
	Zone,
} from "@sitescene/types";
import type {
	EventCreatedEvent,
	EventUpdatedEvent,
} from "@sitescene/types/dist/events";

/**
 * WebSocket event detail type (either created or updated)
 */
export type WebSocketEventDetail = EventCreatedEvent | EventUpdatedEvent;

/**
 * Extended event detail that may include populated relational fields. The
 * broadcast handler enriches the pushed payload with populated
 * installation/zones/measurements and a computed `sensors` array (with
 * geolocation) so air-quality map previews render from the WS event directly.
 */
type ExtendedEventDetail = WebSocketEventDetail & {
	installation?: InstallationDto | string;
	measurements?: Array<Measurement | string>;
	sensors?: EventSensor[];
	zones?: Array<string | Zone>;
};

/**
 * Convert a WebSocket event detail to an Event object for display
 *
 * @param eventDetail - The event detail from WebSocket (EventCreatedEvent or EventUpdatedEvent)
 * @returns Event object compatible with EventItem component
 */
export function convertWebSocketEventToEvent(
	eventDetail: WebSocketEventDetail,
): EventWithSensors {
	// Populated relational fields are present when the broadcast enriched them.
	const detail = eventDetail as ExtendedEventDetail;

	const now = new Date();
	return {
		centroid: eventDetail.centroid,
		// Fallback to current time — real values are preserved by cache merge helpers
		// in websocket.tsx when updating an existing cache entry.
		createdAt: now,
		description: eventDetail.description,
		endTimestamp: new Date(eventDetail.endTimestamp),
		id: eventDetail.id,
		// Use populated installation if present, otherwise fall back to installationId string
		installation: detail.installation ?? eventDetail.installationId,
		// Prefer populated measurements from the enriched broadcast; otherwise
		// fall back to the bare measurement-id refs.
		measurements: detail.measurements ?? eventDetail.measurements,
		// Fallback to empty — real values are preserved by cache merge helpers
		mediaKeys: [],
		// Must be named here or it never reaches the UI: this builder is a
		// hand-written field-by-field literal, so anything not listed is dropped
		// silently. A cached event survives that (the merge spreads `incoming`
		// over `cached`, and a missing key cannot overwrite), but an event that
		// arrives over the socket BEFORE any fetch — exactly the case this flag
		// exists for — has nothing to fall back on and renders as not
		// preliminary until something refetches it.
		preliminary: eventDetail.preliminary,
		// Embedded sensors (with geolocation) from the enriched broadcast, if any.
		sensors: detail.sensors ?? [],
		severity: eventDetail.severity,
		startTimestamp: new Date(eventDetail.startTimestamp),
		status: eventDetail.status as EventStatus,
		// Fallback to empty — real values are preserved by cache merge helpers
		thumbnailKeys: [],
		title: eventDetail.title,
		types: eventDetail.types ?? [],
		// Fallback to current time — real value is preserved by cache merge helpers
		updatedAt: now,
		// Use populated zones if present, otherwise empty array
		zones: detail.zones ?? [],
		// Omit when absent so cache merges do not clobber an existing watches array.
		...(eventDetail.watches !== undefined && { watches: eventDetail.watches }),
	};
}
